import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { useHoldRequest, useLineItems } from '@/hooks/useHoldRequests';
import { LineItemManagement, WorkflowStatusEditor } from '@/components/forms';
import { AuditViewer } from '@/components/audit';
import { LoadingSpinner, ErrorMessage } from '@/components/common';
import { CommentInput, PaginatedCommentHistory } from '@/components/comments';
import { useCreateHoldRequestComment } from '@/hooks/useComments';
import { useAuthContext } from '@/hooks/useAuthContext';
import { formatLocalDate, formatDate, getStatusColor, formatStatus } from '@/lib/utils';
import { websocketClient } from '@/services/websocket-client';
import { useQueryClient } from '@tanstack/react-query';
import type { ActionContext, CreateHoldRequestCommentData } from '@/types';

export default function HoldRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  
  const { data: holdRequest, isLoading, error } = useHoldRequest(id || '');
  const { data: lineItems = [] } = useLineItems(id || '');
  const createComment = useCreateHoldRequestComment();

  // Subscribe to real-time comment updates via WebSocket
  useEffect(() => {
    if (!id) return;

    // Subscribe to hold request updates
    websocketClient.subscribeToHoldRequest(id);

    // Listen for comment events
    const handleCommentUpdate = () => {
      // Invalidate comment queries to refetch
      queryClient.invalidateQueries({
        queryKey: ['holdRequestComments', id],
      });
    };

    // Set up listener for hold request updates (which includes comment events)
    const unsubscribe = websocketClient.onStatusUpdate(
      `hold_request:${id}`,
      handleCommentUpdate
    );

    // Cleanup
    return () => {
      unsubscribe();
      websocketClient.unsubscribeFromHoldRequest(id);
    };
  }, [id, queryClient]);

  // Check if user can create comments on this hold request
  const canCreateComment = useCallback(() => {
    if (!user || !holdRequest) return false;

    const userRole = user.role;
    
    // Sales AE can comment on their own hold requests
    if (userRole === 'sales_ae' && holdRequest.salesAeId === user.id) {
      return true;
    }

    // Service AE can comment on cross-region requests where they're assigned to line items
    // Note: Service AE is now at line item level, so we check if they have any assigned line items
    if (userRole === 'service_ae' && holdRequest.isCrossRegion) {
      // They can comment if they're assigned to any line items (checked server-side)
      return true;
    }

    // Charting team members can comment on assigned hold requests
    if (
      (userRole === 'charting_member' || userRole === 'charting_manager') &&
      holdRequest.assignedUserId === user.id
    ) {
      return true;
    }

    // Exceptions team can comment on exception holds
    if (userRole === 'exceptions_member' && holdRequest.exceptionHoldsCount > 0) {
      return true;
    }

    // Leadership roles can comment on any hold request
    if (
      userRole === 'sales_vp' ||
      userRole === 'gvp' ||
      userRole === 'rvp' ||
      userRole === 'adops_leadership'
    ) {
      return true;
    }

    return false;
  }, [user, holdRequest]);

  // Check if user can view comments on this hold request
  const canViewComments = useCallback(() => {
    if (!user || !holdRequest) return false;

    const userRole = user.role;
    
    // Sales AE can view comments on their own hold requests
    if (userRole === 'sales_ae' && holdRequest.salesAeId === user.id) {
      return true;
    }

    // Service AE can view comments on cross-region requests where they're assigned to line items
    // Note: Service AE is now at line item level
    if (userRole === 'service_ae' && holdRequest.isCrossRegion) {
      return true;
    }

    // Charting team members can view comments on hold requests in their scope
    if (userRole === 'charting_member' || userRole === 'charting_manager') {
      return true;
    }

    // Exceptions team can view comments on exception holds
    if (userRole === 'exceptions_member') {
      return true;
    }

    // Leadership roles can view comments on any hold request
    if (
      userRole === 'sales_vp' ||
      userRole === 'gvp' ||
      userRole === 'rvp' ||
      userRole === 'adops_leadership'
    ) {
      return true;
    }

    return false;
  }, [user, holdRequest]);

  // Handle comment submission
  const handleCommentSubmit = async (text: string, actionContext?: ActionContext) => {
    if (!id) return;

    const commentData: CreateHoldRequestCommentData = {
      holdRequestId: id,
      commentText: text,
    };

    // Only add actionContext if it's defined
    if (actionContext) {
      commentData.actionContext = actionContext;
    }

    await createComment.mutateAsync(commentData);
  };

  // Helper function to safely format dates using UTC to avoid timezone shifts

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !holdRequest) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message="Failed to load hold request. Please try again." />
        <Button onClick={() => navigate('/')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  // OPE-386 (c): route label and colour through the shared helpers so the
  // cancelled override lives in one place (formatStatus / getStatusColor).
  const getStatusBadge = (derivedStatus: string, workflowStatus?: string) => {
    return (
      <Badge className={getStatusColor(derivedStatus, workflowStatus)}>
        {formatStatus(derivedStatus, workflowStatus).toUpperCase()}
      </Badge>
    );
  };

  const getCategoryBadge = (category: string) => {
    const categoryColors: Record<string, string> = {
      movie_studio: 'bg-purple-100 text-purple-800',
      standard: 'bg-outfront-purple-light text-outfront-purple',
      premium: 'bg-green-100 text-green-800',
    };
    return (
      <Badge className={categoryColors[category] || 'bg-neutral-100 text-neutral-900'}>
        {category.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 hover:bg-neutral-100"
        >
          ← Back to Dashboard
        </Button>

        {/* Hero Section */}
        <div className="bg-white rounded-lg  border border-neutral-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-neutral-900 mb-4">
                Hold Request #{holdRequest.requestNumber}
              </h1>
              
              {/* Status Badges with Clear Labels */}
              <div className="flex items-start gap-6 flex-wrap mb-4">
                <div>
                  <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1.5">
                    Completion Status
                  </div>
                  {getStatusBadge(holdRequest.derivedStatus, holdRequest.workflowStatus)}
                </div>
                <div>
                  <WorkflowStatusEditor
                    holdRequestId={holdRequest.id}
                    currentStatus={holdRequest.workflowStatus}
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1.5">
                    Category
                  </div>
                  {getCategoryBadge(holdRequest.businessCategory)}
                </div>
                {holdRequest.isCrossRegion && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1.5">
                      Type
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-800">
                      Cross-Region
                    </Badge>
                  </div>
                )}
                {holdRequest.isAutoPerm && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-1.5">
                      Hold Type
                    </div>
                    <Badge className="bg-outfront-purple-light text-outfront-purple">
                      Auto-Perm
                    </Badge>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-neutral-400">
                Created {formatLocalDate(holdRequest.createdAt)}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowAuditTrail(!showAuditTrail)}
            >
              {showAuditTrail ? 'Hide' : 'Show'} Change History
            </Button>
          </div>

          {/* Movie Studio Warning */}
          {holdRequest.businessCategory === 'movie_studio' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Movie Studio Requirements:</strong> All line items must maintain a minimum 
                60 business-day gap between hold expiration date and flight start date.
              </p>
            </div>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Column - Request Details (2 columns wide) */}
          <div className="lg:col-span-2">
            {/* Request Details */}
            <Card>
              <CardHeader>
                <CardTitle>Request Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Advertiser</label>
                    <p className="text-lg font-semibold text-neutral-900 mt-1">{holdRequest.advertiser}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Business Category</label>
                    <p className="text-lg font-semibold text-neutral-900 mt-1 capitalize">
                      {holdRequest.businessCategory.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Sales AE</label>
                    <p className="text-base text-neutral-900 mt-1">
                      {holdRequest.salesAe 
                        ? `${holdRequest.salesAe.firstName} ${holdRequest.salesAe.lastName}`
                        : <span className="text-neutral-400 italic">Not assigned</span>}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Hold Start Date</label>
                    <p className="text-base text-neutral-900 mt-1">
                      {holdRequest.holdStartDate ? formatDate(holdRequest.holdStartDate) : 'N/A'}
                    </p>
                  </div>
                  {holdRequest.isCrossRegion && (
                    <div>
                      <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Cross-Region</label>
                      <p className="text-base text-neutral-900 mt-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-outfront-purple-light text-outfront-purple">
                          Service AE assigned per line item
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats & Quick Info */}
          <div className="space-y-6">
            {/* Line Item Stats */}
            {lineItems.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Line Item Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-2 bg-neutral-50 rounded-lg">
                    <div className="text-3xl font-bold text-neutral-900">{lineItems.length}</div>
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mt-1">Total Line Items</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center py-3 bg-outfront-purple-light rounded-lg border border-outfront-purple">
                      <div className="text-2xl font-bold text-outfront-purple">
                        {lineItems.filter(item => !item.isExceptionHold).length}
                      </div>
                      <div className="text-xs font-semibold text-outfront-purple uppercase tracking-wide mt-1">Standard</div>
                    </div>
                    <div className="text-center py-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="text-2xl font-bold text-purple-900">
                        {lineItems.filter(item => item.isExceptionHold).length}
                      </div>
                      <div className="text-xs font-semibold text-purple-700 uppercase tracking-wide mt-1">Exception</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Line Item Management - Full Width */}
        <div className="mb-6">
          <LineItemManagement
            holdRequestId={holdRequest.id}
            businessCategory={holdRequest.businessCategory}
            readOnlyHoldPosition={true}
          />
        </div>

        {/* Audit Trail */}
        {showAuditTrail && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <AuditViewer
                tableName="hold_requests"
                recordId={holdRequest.id}
                title="Hold Request Change History"
              />
            </CardContent>
          </Card>
        )}

        {/* Comments Section */}
        {canViewComments() && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Comments & Activity</CardTitle>
                <Button
                  variant="ghost"
                  onClick={() => setShowComments(!showComments)}
                  className="text-sm"
                >
                  {showComments ? 'Hide' : 'Show'}
                </Button>
              </div>
            </CardHeader>
            {showComments && (
              <CardContent className="space-y-6">
                {/* Comment Input - Only show if user has permission */}
                {canCreateComment() && (
                  <div className="pb-6 border-b border-neutral-200">
                    <CommentInput
                      onSubmit={handleCommentSubmit}
                      placeholder="Add a comment to this hold request..."
                      disabled={createComment.isPending}
                    />
                  </div>
                )}

                {/* Comment History */}
                <div>
                  <PaginatedCommentHistory
                    holdRequestId={holdRequest.id}
                    pageSize={20}
                    enableCollapsible={true}
                    initialCollapsedCount={5}
                    recentThresholdMinutes={5}
                  />
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
